import { useState } from 'react';
import { Copy, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Label } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Dialog } from '@/components/ui/Dialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { useIngressKeys, useIngressKeyActions } from '@/hooks/useDispatcher';
import { formatDateTime } from '@/lib/utils';

const ALL_SCOPES = ['jobs:write', 'jobs:read', 'contacts:read', 'admin'];

export default function KeysPage() {
  const keys = useIngressKeys();
  const { create, remove } = useIngressKeyActions();
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['jobs:write']);
  const [created, setCreated] = useState<{ secret: string; name: string } | null>(null);

  function toggleScope(s: string) {
    setScopes((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  }

  async function handleCreate() {
    if (!name.trim()) return;
    try {
      const res = await create.mutateAsync({ name: name.trim(), scopes });
      setCreated({ secret: res.secret, name: res.key.name });
      setName('');
      setScopes(['jobs:write']);
      setShowCreate(false);
    } catch {
      toast.error('Création impossible');
    }
  }

  async function handleDelete(id: string, label: string) {
    if (!confirm(`Révoquer la clé "${label}" ?`)) return;
    await remove.mutateAsync(id);
    toast.success('Clé révoquée');
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={() => setShowCreate(true)}>
          <Plus size={14} /> Nouvelle clé
        </Button>
      </div>

      <Card>
        <CardHeader title="Clés d'ingress" subtitle="Authentification X-API-Key des sources" />
        {keys.isLoading ? (
          <div className="p-5">
            <Skeleton className="h-32 w-full" />
          </div>
        ) : !keys.data?.length ? (
          <EmptyState title="Aucune clé créée" />
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-brand-border">
              <tr>
                <th className="th">Nom</th>
                <th className="th">Préfixe</th>
                <th className="th">Scopes</th>
                <th className="th">Dernière utilisation</th>
                <th className="th">Créée</th>
                <th className="th">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {keys.data.map((k) => (
                <tr key={k.id}>
                  <td className="td font-medium text-brand-dark">{k.name}</td>
                  <td className="td font-mono text-xs">{k.prefix}…</td>
                  <td className="td">
                    <div className="flex flex-wrap gap-1">
                      {k.scopes.map((s) => (
                        <Badge key={s} tone="brand">
                          {s}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="td">{formatDateTime(k.last_used_at)}</td>
                  <td className="td">{formatDateTime(k.created_at)}</td>
                  <td className="td">
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(k.id, k.name)}>
                      <Trash2 size={12} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="Nouvelle clé d'ingress"
        description="La valeur ne sera affichée qu'une seule fois."
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowCreate(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreate} loading={create.isPending}>
              Créer
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <Label>Nom</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="getfishnet-prod" />
          </div>
          <div>
            <Label>Scopes</Label>
            <div className="flex flex-wrap gap-2">
              {ALL_SCOPES.map((s) => {
                const active = scopes.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleScope(s)}
                    className={
                      active
                        ? 'badge bg-brand-primary text-white cursor-pointer'
                        : 'badge bg-gray-100 text-gray-600 cursor-pointer hover:bg-gray-200'
                    }
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={!!created}
        onClose={() => setCreated(null)}
        title="Copiez la clé maintenant"
        description="Elle ne sera plus jamais affichée."
        footer={
          <Button onClick={() => setCreated(null)}>J'ai copié</Button>
        }
      >
        {created && (
          <div className="space-y-3">
            <div className="text-xs text-gray-500">{created.name}</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-xs bg-gray-50 border border-brand-border rounded-brand px-3 py-2 break-all">
                {created.secret}
              </code>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(created.secret);
                  toast.success('Copié');
                }}
              >
                <Copy size={12} /> Copier
              </Button>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}
